import React, { type FC, useEffect, useRef, useState } from "react";
import CookieConsent from "react-cookie-consent";
import { useTranslation } from "react-i18next";

import { useChatBottomInset } from "@hooks/useChatBottomInset";
import {
  useConfigurationState,
  useConfigurationStore,
} from "@stores/configuration";
import { pushConsentAccepted } from "@utilities/awin";
import { cn } from "@utilities/helper";
import { hasCookieConsent, initMetaPixel } from "@utilities/meta-pixel";

const COOKIE_BAR_ID = "cookie-consent-bar";

const CookieConsentBanner: FC = () => {
  const { t } = useTranslation();
  const configurationState = useConfigurationState();
  const hasAccepted = configurationState.hasAcceptedCookieConsent;

  // The configuration store is persisted with `skipHydration`, so wait until it
  // has rehydrated before deciding to show the bar — otherwise an already
  // accepted user would briefly see it flash on load.
  const [isHydrated, setIsHydrated] = useState(
    useConfigurationStore.persist.hasHydrated(),
  );

  useEffect(() => {
    if (isHydrated) {
      return;
    }
    return useConfigurationStore.persist.onFinishHydration(() => {
      setIsHydrated(true);
    });
  }, [isHydrated]);

  // The bar is rendered by react-cookie-consent (no ref): find it by id so
  // the chat button can move above it while it shows.
  const isVisible = isHydrated && !hasAccepted;
  const bannerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    bannerRef.current = isVisible
      ? document.getElementById(COOKIE_BAR_ID)
      : null;
  }, [isVisible]);
  useChatBottomInset(bannerRef, isVisible);

  useEffect(() => {
    if (!isHydrated) {
      return;
    }
    // Migrate users who previously accepted via the cookie only into the
    // persisted store so they never see the bar again once the cookie expires.
    if (!hasAccepted && hasCookieConsent()) {
      configurationState.setHasAcceptedCookieConsent(true);
      return;
    }
    if (hasAccepted) {
      initMetaPixel();
      pushConsentAccepted();
    }
  }, [isHydrated, hasAccepted]);

  return (
    <CookieConsent
      debug={false}
      visible={isVisible ? "show" : "hidden"}
      customContainerAttributes={{ id: COOKIE_BAR_ID }}
      buttonText={t("cookie_consent_accept_btn_text")}
      onAccept={() => {
        configurationState.setHasAcceptedCookieConsent(true);
        initMetaPixel();
        pushConsentAccepted();
      }}
      containerClasses={cn(
        "px-5 gap-4 py-4 !items-start !bg-black/80 backdrop-blur-sm",
      )}
      buttonClasses={cn("custom-btn-primary")}
      contentClasses={cn("!m-0")}
      disableButtonStyles={true}
    >
      {t("cookie_consent_desc")}
    </CookieConsent>
  );
};

export default CookieConsentBanner;
